import { createContext, useCallback, useContext } from 'react'

import { useAppDispatch, useAppSelector } from '@/store'
import { logout as reduxLogout } from '@/store/authSlice'
import type { AuthUser } from '@/store/authSlice'
import { setDeliveryInstructions } from '@/store/ticketSlice'

export type { AuthUser }

interface AuthContextValue {
	isAuthenticated: boolean
	user: AuthUser | null
	logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
	children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
	const dispatch = useAppDispatch()
	const user = useAppSelector((state) => state.auth.user)

	const logout = useCallback((): Promise<void> => {
		dispatch(reduxLogout())
		// Clears from Redux and localStorage so restoreCart cannot rehydrate stale instructions for a guest session.
		dispatch(setDeliveryInstructions(null))
		return Promise.resolve()
	}, [dispatch])

	return (
		<AuthContext.Provider
			value={{
				isAuthenticated: user !== null,
				user,
				logout,
			}}
		>
			{children}
		</AuthContext.Provider>
	)
}

export function useAuth(): AuthContextValue {
	const ctx = useContext(AuthContext)
	if (!ctx) throw new Error('useAuth must be used within AuthProvider')
	return ctx
}
